/**
 * Pilot patience: a pilot who asked for something and hears nothing calls again, then presses,
 * then acts on their own. The sim loop owns the side effects (radio, score, hold, return to
 * stand); this file owns the request record, the timing table and the words. Pure; no sim state.
 */
import type { AirportData } from '@atc/core';
import type { Aircraft } from './aircraft';
import type { Instruction } from './commands';
import { altitudeSpeech, altitudeText, callsignPhrase, fixSpeech, pilotPhrase, runwaySpeech, type Phrase } from './comms';

export type RequestKind = 'contact' | 'departure' | 'lineUp' | 'approach' | 'descent' | 'climb' | 'vectors' | 'landing';
/** 0 asked, 1 second call, 2 pressing, 3 acted on their own. */
export type PatienceStage = 0 | 1 | 2 | 3;

export interface PilotRequest {
  kind: RequestKind;
  /** When the pilot first asked; the radar tag counts from here. */
  askedAtS: number;
  /** Stand by: no escalation (and no routine repeat) before this time. */
  deferredUntilS: number;
  stage: PatienceStage;
  /** When the current stage began, or the controller last answered. */
  stageAtS: number;
  standbys: number;
  /** Not said yet (a go-around asks for vectors only once it is climbing away). */
  unvoiced?: boolean;
}

export function newRequest(kind: RequestKind, nowS: number, deferS = 0, unvoiced = false): PilotRequest {
  return { kind, askedAtS: nowS, deferredUntilS: nowS + deferS, stage: 0, stageAtS: nowS, standbys: 0, ...(unvoiced ? { unvoiced } : {}) };
}

/**
 * ASSUMPTIONS, tuned for play rather than taken from any rulebook: seconds of silence before
 * stages 1, 2 and 3, each counted from the previous stage (stage 1 from the call, the last
 * answer or the end of a stand-by). Null: the pilot never gets there by waiting; a landing
 * without clearance already ends in the go-around at 1 nm. Realism scales the table
 * (`RealismOptions.pilotPatienceScale`).
 */
export const PATIENCE_S: Record<RequestKind, readonly [number, number, number | null]> = {
  contact: [20, 40, 60],
  departure: [90, 90, 120],
  lineUp: [45, 45, 60],
  approach: [60, 60, 90],
  vectors: [60, 60, 90],
  descent: [60, 60, 90],
  climb: [60, 60, null],
  landing: [15, 15, null],
};
/** Stand by defers a request this long, at most this many times per request. */
export const STANDBY_S = 120;
export const STANDBY_LIMIT = 2;

/** What the pilot is waiting for: a three-letter radar tag and plain words. */
export const NEEDS: Record<RequestKind, { code: string; text: string }> = {
  contact: { code: 'CTC', text: 'an answer to the check-in' },
  departure: { code: 'DEP', text: 'take-off clearance' },
  lineUp: { code: 'LUW', text: 'take-off clearance' },
  approach: { code: 'APP', text: 'approach clearance' },
  vectors: { code: 'VEC', text: 'vectors' },
  descent: { code: 'DES', text: 'descent' },
  climb: { code: 'CLB', text: 'climb instructions' },
  landing: { code: 'LND', text: 'landing clearance' },
};

export const isDepartureRequest = (kind: RequestKind) => kind === 'departure' || kind === 'lineUp';

/** The stage the request has reached by `nowS`, or null when nothing is due. */
export function dueStage(req: PilotRequest, nowS: number, scale = 1): PatienceStage | null {
  if (req.unvoiced || req.stage >= 3) return null;
  const limit = PATIENCE_S[req.kind][req.stage as 0 | 1 | 2];
  if (limit === null || limit === undefined) return null;
  return nowS - Math.max(req.stageAtS, req.deferredUntilS) >= limit * scale ? ((req.stage + 1) as PatienceStage) : null;
}

/** The controller spoke to this aircraft. A check-in is answered; anything else starts waiting afresh, without undoing penalties. */
export function acknowledgeRequest(memory: { request?: PilotRequest }, nowS: number): void {
  const r = memory.request;
  if (!r) return;
  if (r.kind === 'contact') { memory.request = undefined; return; }
  r.stage = 0; r.stageAtS = nowS;
}

/** What accepted instructions do to a request: satisfy it, turn a departure into a line-up wait, or nothing. */
export function requestOutcome(kind: RequestKind, flown: readonly Instruction[]): 'clear' | 'lineUp' | null {
  const has = (...kinds: Instruction['kind'][]) => flown.some((i) => kinds.includes(i.kind));
  switch (kind) {
    case 'contact': return flown.length ? 'clear' : null;
    case 'vectors': return has('heading', 'direct', 'hold', 'approach', 'land', 'resumeNav') ? 'clear' : null;
    case 'approach': return has('approach', 'land', 'hold') ? 'clear' : null;
    case 'descent': return has('altitude') ? 'clear' : null;
    case 'climb': return has('altitude') ? 'clear' : null;
    case 'landing': return has('land', 'abort') ? 'clear' : null;
    case 'departure': return has('takeoff') ? 'clear' : has('lineUp') ? 'lineUp' : null;
    case 'lineUp': return has('takeoff') ? 'clear' : null;
  }
}

const p = (text: string, speech = text): Phrase => ({ text, speech });

/**
 * What the pilot says at a stage. `holdFix` is where an arrival holds at stage 3 (none: it keeps
 * its heading). Stations follow the check-in: approach for arrivals, tower for departures and
 * aircraft on final.
 */
export function patiencePhrase(ac: Aircraft, req: PilotRequest, stage: 1 | 2 | 3, airport: AirportData, telephony?: Record<string, string>, holdFix?: string): Phrase {
  const cs = callsignPhrase(ac, telephony);
  const rw = ac.approach?.runway.name ?? ac.arrivalRunway?.name ?? ac.departureRunway?.name ?? airport.arrivalRunway;
  const station = isDepartureRequest(req.kind) || req.kind === 'landing' ? airport.radio.twr : airport.radio.app;
  const call = (body: Phrase): Phrase => ({ text: `${station}, ${cs.text}, ${body.text}`, speech: `${station}, ${cs.speech}, ${body.speech}` });
  const ask: Record<RequestKind, Phrase> = {
    contact: p(`${altitudeText(Math.round(ac.altitudeFt / 100) * 100)}, inbound`, `${altitudeSpeech(Math.round(ac.altitudeFt / 100) * 100)}, inbound`),
    departure: p(`holding short runway ${rw}, ready for departure`, `holding short runway ${runwaySpeech(rw)}, ready for departure`),
    lineUp: p(`lined up runway ${rw}, ready for departure`, `lined up runway ${runwaySpeech(rw)}, ready for departure`),
    approach: p(`request ILS approach runway ${rw}`, `request I L S approach runway ${runwaySpeech(rw)}`),
    vectors: p('request vectors'),
    descent: p('request descent'),
    climb: p('request climb instructions'),
    landing: p(`short final runway ${rw}, request landing clearance`, `short final runway ${runwaySpeech(rw)}, request landing clearance`),
  };
  const a = ask[req.kind];
  if (stage === 1) return call({ text: `second call, ${a.text}`, speech: `second call, ${a.speech}` });
  if (stage === 2) {
    if (req.kind === 'contact') return call(p('how do you read, requesting instructions'));
    if (isDepartureRequest(req.kind)) return call(p('still waiting for departure, we will return to stand if we cannot go soon'));
    if (req.kind === 'climb') return call(p('remaining level, still requesting climb instructions'));
    if (req.kind === 'landing') return call({ text: `no landing clearance runway ${rw}, we will go around`, speech: `no landing clearance runway ${runwaySpeech(rw)}, we will go around` });
    return call({ text: `${a.text}, unable to continue without instructions`, speech: `${a.speech}, unable to continue without instructions` });
  }
  if (isDepartureRequest(req.kind)) return pilotPhrase(p('no departure clearance, returning to stand'), cs);
  return pilotPhrase(holdFix ? { text: `no instructions received, entering hold at ${holdFix}`, speech: `no instructions received, entering hold at ${fixSpeech(holdFix)}` } : p('no instructions received, maintaining present heading and altitude'), cs);
}
